import { merge } from 'lodash';
import { normalize } from 'normalizr';

import { IUser } from '@sravni/types/lib/auth';

import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { ICommentCreateBody, ICommentItem, ICommentsListDecorated, ICommentsListQuery } from '../../interfaces/reviews';
import { comment as commentSchema } from '../../schemas/reviews';
import { createComment, loadCommentsList, updateComment } from '../../services/comments';
import { sortArray } from '../../utils/sort';

import { setCompanyCommentId } from './reviews';

export interface IState {
  comments: {
    [id: number]: ICommentItem;
  };
  commentsList: {
    list: ICommentItem['id'][];
    commentCompanyId?: number;
    loading?: boolean;
    loadingError?: any;
    total?: number;
    pageSize?: number;
  };
  postComment: {
    postComment?: string | boolean;
    loading?: boolean;
    loadingError?: any;
  };
}

type Normalized = {
  entities: {
    comments: IState['comments'];
  };
  result: IState['commentsList']['list'];
};

const initialState: IState = {
  comments: {},
  commentsList: {
    list: [],
    commentCompanyId: null,
    loading: false,
    loadingError: null,
  },
  postComment: {
    postComment: '',
    loading: false,
    loadingError: null,
  },
};

const name = 'comments';

const slice = createSlice({
  name,
  initialState,
  reducers: {
    setCommentsList: (state, { payload }: PayloadAction<ICommentsListDecorated>) => {
      const { parentIdCheck, allComments, id, organizationId } = payload;

      const companiesAnswers = allComments.items?.filter(
        (item) => item.authorRole === 'company' && item.organizationId.toString() === organizationId.toString(),
      );

      const commentCompanyId =
        companiesAnswers.find((item) => item.isMain)?.id ||
        sortArray(companiesAnswers, 'date', false, true)[0]?.id ||
        null;
      const normalized: Normalized = normalize(allComments.items, [commentSchema]);

      state = merge(state, normalized.entities);

      if (parentIdCheck) {
        state.comments[id].comments = normalized.result;
      } else {
        state.commentsList.commentCompanyId = commentCompanyId;
        setCompanyCommentId({ commentCompanyId }); // TODO: check if this line works
      }

      state.commentsList.pageSize = -1;
      state.commentsList.total = parentIdCheck ? state.commentsList.total : allComments.total;
      state.commentsList.list = parentIdCheck ? state.commentsList.list : normalized.result;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCommentsList.fulfilled, (state, { payload }) => {
        const { parentIdCheck, allComments, id, organizationId } = payload;

        const companiesAnswers = allComments.items?.filter(
          (item) => item.authorRole === 'company' && item.organizationId.toString() === organizationId.toString(),
        );

        const commentCompanyId =
          companiesAnswers.find((item) => item.isMain)?.id ||
          sortArray(companiesAnswers, 'date', false, true)[0]?.id ||
          null;
        const normalized: Normalized = normalize(allComments.items, [commentSchema]);

        state = merge(state, normalized.entities);

        if (parentIdCheck) {
          state.comments[id].comments = normalized.result;
        } else {
          state.commentsList.commentCompanyId = commentCompanyId;
          setCompanyCommentId({ commentCompanyId }); // TODO: check if this line works
        }

        state.commentsList.pageSize = -1;
        state.commentsList.total = parentIdCheck ? state.commentsList.total : allComments.total;
        state.commentsList.list = parentIdCheck ? state.commentsList.list : normalized.result;
        state.commentsList.loading = false;
      })
      .addCase(fetchCommentsList.pending, (state) => {
        state.commentsList.loading = true;
      })
      .addCase(fetchCommentsList.rejected, (state, { error }) => {
        state.commentsList.loading = true;
        state.commentsList.loadingError = error;

        throw error;
      })

      .addCase(doCreateComment.fulfilled, (state) => {
        state.postComment.loadingError = null;
        state.postComment.loading = false;

        // TODO: чекнуть нужно ли это делать

        // если мы добавили комент на комент, увеличиваем commentCount у родительского коммента
        // ...(parentId && {
        //   comments: {
        //     ...state.comments,
        //     [parentId]: {
        //       ...state.comments[parentId],
        //       commentsCount: state.comments[parentId].commentsCount + 1,
        //     },
        //   },
        // }),
      })
      .addCase(doCreateComment.pending, (state) => {
        state.postComment.loadingError = null;
        state.postComment.loading = true;
      })
      .addCase(doCreateComment.rejected, (state, { error }) => {
        state.postComment.loadingError = error;
        state.postComment.loading = false;

        throw error;
      })

      .addCase(doUpdateComment.rejected, (_state, { error }) => {
        throw error;
      });
  },
});

const { actions, reducer } = slice;

export const { setCommentsList } = actions;

export const fetchCommentsList = createAsyncThunk<ICommentsListDecorated, ICommentsListQuery & { user?: IUser }>(
  `${name}/fetchCommentsList`,
  async (payload) => {
    return await loadCommentsList(payload);
  },
);

export const doCreateComment = createAsyncThunk<void, ICommentCreateBody>(
  `${name}/doCreateComment`,
  async (payload) => {
    await createComment(payload);
  },
);

export const doUpdateComment = createAsyncThunk<void, ICommentCreateBody>(
  `${name}/doUpdateComment`,
  async (payload) => {
    await updateComment(payload);
  },
);

export default reducer;
